import { Card, Badge, Space, Typography } from "antd";
import { WifiOutlined, DisconnectOutlined } from "@ant-design/icons";
import EquipmentCard, {
  EquipmentStatus,
  getStatusConfig,
} from "common/components/widgets/equipments/EquipmentCard";

const { Text } = Typography;

interface DispensarioCardProps {
  id: string;
  codigo: string;
  serialNumber?: string;
  estado: EquipmentStatus;
  ultimaComunicacion?: string;
  viewMode?: "grid" | "list";
}

// Icono inline con fill blanco para que contraste con el fondo azul
const GasIcon = ({ size = 40 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 57 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M33.75 11.25C33.75 10.2144 32.9106 9.375 31.875 9.375H13.125C12.0894 9.375 11.25 10.2144 11.25 11.25V26.25C11.25 27.2856 12.0894 28.125 13.125 28.125H31.875C32.9106 28.125 33.75 27.2856 33.75 26.25V11.25ZM30 24.375H15V13.125H30V24.375Z"
      fill="white"
    />
    <path
      d="M55.2584 13.2953L47.7807 9.54527C46.8524 9.08016 45.7393 9.45832 45.2761 10.3839C44.8129 11.3104 45.1938 12.4365 46.1203 12.8998L48.9681 14.3223C48.8919 14.5262 48.7934 14.7418 48.7934 14.9726C48.7934 17.4138 50.625 19.4756 52.5 20.252V43.0976C52.5 44.1312 51.6586 44.9726 50.625 44.9726C49.5914 44.9726 48.75 44.1312 48.75 43.0976V28.0976C48.75 23.5705 45 19.7832 41.25 18.9121V7.47258C41.25 3.33715 37.975 0 33.8395 0H11.3395C7.2041 0 3.75 3.33715 3.75 7.47258V49.4385L1.03641 50.7953C0.401016 51.113 0 51.7621 0 52.4726V58.0976C0 59.133 0.929062 60 1.96453 60H43.2145C44.25 60 45 59.133 45 58.0976V52.4726C45 51.7621 44.599 51.113 43.9636 50.7953L41.25 49.4385V22.8182C43.125 23.5945 45 25.6563 45 28.0976V43.0976C45 46.1994 47.5232 48.7226 50.625 48.7226C53.7268 48.7226 56.25 46.1994 56.25 43.0976V14.9726C56.25 14.2621 55.8937 13.613 55.2584 13.2953ZM41.25 56.25H3.75V53.6316L6.46359 52.2748C7.09898 51.9571 7.5 51.308 7.5 50.5975V7.47246C7.5 5.40434 9.27129 3.75 11.3395 3.75H33.8395C35.9077 3.75 37.5 5.40434 37.5 7.47258V50.5976C37.5 51.3081 37.901 51.9572 38.5364 52.2749L41.25 53.6316V56.25Z"
      fill="white"
    />
  </svg>
);

export function DispensaryCard({
  codigo,
  serialNumber,
  estado,
  ultimaComunicacion,
  viewMode = "grid",
}: DispensarioCardProps) {
  const statusConfig = getStatusConfig(estado);
  const isOnline = statusConfig.online;

  if (viewMode === "list") {
    return (
      <Card
        size="small"
        style={{ opacity: isOnline ? 1 : 0.75 }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ display: "flex", alignItems: "stretch" }}>
          <div
            style={{
              background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 120,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <GasIcon size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <Text
                strong
                style={{ color: "white", display: "block", fontSize: 13 }}
              >
                {codigo}
              </Text>
              <Text
                style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}
                ellipsis
              >
                {serialNumber ?? "Sin serie"}
              </Text>
            </div>
          </div>

          {/* Contenido */}
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              gap: 12,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <Space size="small">
              {isOnline ? (
                <WifiOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              ) : (
                <DisconnectOutlined
                  style={{ color: "#ff4d4f", fontSize: 14 }}
                />
              )}
              <Badge
                status={statusConfig.color}
                text={<span style={{ fontSize: 12 }}>{statusConfig.text}</span>}
              />
            </Space>

            <Text
              type="secondary"
              style={{ fontSize: 11, display: "none" }}
              className="last-comm-desktop"
            >
              Última:{" "}
              {ultimaComunicacion
                ? new Date(ultimaComunicacion).toLocaleString("es-MX")
                : "-"}
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  // Grid View
  return (
    <EquipmentCard
      status={estado}
      icon={<GasIcon size={36} />}
      isActive={isOnline}
      content={
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 16, display: "block" }}>
            {codigo}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {serialNumber ?? "Sin número de serie"}
          </Text>
        </div>
      }
      footer={
        <Text type="secondary" style={{ fontSize: 12 }}>
          Última comunicación:{" "}
          <Text style={{ fontSize: 12 }}>
            {ultimaComunicacion
              ? new Date(ultimaComunicacion).toLocaleString("es-MX")
              : "-"}
          </Text>
        </Text>
      }
    />
  );
}
